/**
 * Login Screen Component - TUI-styled password gate
 */

import { useState, useCallback, useEffect } from 'react'

interface LoginScreenProps {
  onLogin: (token: string) => void
}

interface LoginSuccess {
  token: string
  message: string
  remaining?: number
  limit?: number
  used?: number
  unlimited?: boolean
}

export function LoginScreen({ onLogin }: LoginScreenProps) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [loginSuccess, setLoginSuccess] = useState<LoginSuccess | null>(null)

  // Check for existing session on mount
  useEffect(() => {
    const token = localStorage.getItem('pe_session_token')
    if (token) {
      // Verify token is still valid
      verifyToken(token)
    }
  }, [])

  const verifyToken = async (token: string) => {
    try {
      const response = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await response.json()
      if (data.valid) {
        onLogin(token)
      } else {
        localStorage.removeItem('pe_session_token')
      }
    } catch {
      // Token invalid or network error, stay on login screen
      localStorage.removeItem('pe_session_token')
    }
  }

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!password.trim()) {
      setError('Please enter a password')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: password.trim() }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        localStorage.setItem('pe_session_token', data.token)

        // Show success message with remaining queries
        setLoginSuccess({
          token: data.token,
          message: data.message,
          remaining: data.remaining,
          limit: data.limit,
          used: data.used,
          unlimited: data.unlimited === true,
        })
      } else {
        setError(data.error || 'Invalid password')
        setPassword('')
      }
    } catch {
      setError('Connection error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [password])

  const handleContinue = useCallback(() => {
    if (loginSuccess) {
      onLogin(loginSuccess.token)
    }
  }, [loginSuccess, onLogin])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (loginSuccess) {
        handleContinue()
      } else {
        handleSubmit(e)
      }
    }
  }, [handleSubmit, handleContinue, loginSuccess])

  return (
    <div className="h-screen flex flex-col bg-term-bgDark">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-2 border-b border-term-border bg-term-bgMedium">
        <div className="flex items-center gap-3">
          <span className="text-term-accent font-bold text-lg">PE Diligence</span>
          <span className="text-term-textDim">|</span>
          <span className="text-term-textDim">Access Portal</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="term-box p-6">
            {loginSuccess ? (
              // Success Screen with remaining logins
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-term-green text-2xl mb-2">✓</div>
                  <h2 className="text-term-text font-bold text-lg">Access Granted</h2>
                  <p className="text-term-textDim text-sm mt-2">{loginSuccess.message}</p>
                </div>

                {!loginSuccess.unlimited && loginSuccess.remaining !== undefined && (
                  <div className="bg-term-bgDark border border-term-border rounded p-3 text-center">
                    <span className="text-term-textDim text-sm">Company queries remaining: </span>
                    <span className={`font-bold ${loginSuccess.remaining <= 10 ? 'text-term-yellow' : 'text-term-green'}`}>
                      {loginSuccess.remaining}
                    </span>
                    {loginSuccess.limit && (
                      <span className="text-term-textDim text-sm"> / {loginSuccess.limit}</span>
                    )}
                  </div>
                )}

                <button
                  onClick={handleContinue}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  className="w-full bg-term-accent text-term-bgDark font-bold py-2 px-4 rounded
                           hover:bg-term-accentBright transition-colors"
                >
                  [Enter] Continue to Dashboard
                </button>
              </div>
            ) : (
              // Login Form
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-term-textDim text-sm mb-2">
                    <span className="text-term-green">$</span> Enter access code:
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full bg-term-bgDark border border-term-border rounded px-3 py-2
                             text-term-text font-mono focus:outline-none focus:border-term-accent
                             placeholder-term-textDim"
                    placeholder="••••••••"
                    autoFocus
                    disabled={isLoading}
                  />
                </div>

                {error && (
                  <div className="text-term-red text-sm font-mono">
                    <span className="text-term-red">✗</span> {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-term-accent text-term-bgDark font-bold py-2 px-4 rounded
                           hover:bg-term-accentBright transition-colors disabled:opacity-50
                           disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Authenticating...' : '[Enter] Access System'}
                </button>
              </form>
            )}

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-term-border text-center">
              <p className="text-term-textDim text-xs">
                Authorized access only. All sessions are logged.
              </p>
              <p className="mt-2">
                <a 
                  href="/admin.html" 
                  className="text-term-textDim text-xs hover:text-term-text underline"
                >
                  Admin
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Status Bar */}
      <footer className="px-4 py-1 bg-term-bgMedium border-t border-term-border text-xs">
        <div className="flex justify-between text-term-textDim">
          <span>Secure Connection</span>
          <span>© 2026 W4M.ai</span>
        </div>
      </footer>
    </div>
  )
}
